import { Dtos, type SuretyAccountId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { logError } from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import { decisionsQueryKey } from "../decisioning.keys";
import { type Decision } from "../types";

/**
 * A create must never repeat on its own, so it skips the app's default retries. Failures are
 * logged here and shown inline by the caller (per-call `onError`), not in the global error modal.
 */
export const useCreateDecision = (accountId: SuretyAccountId) => {
	const queryClient = useQueryClient();
	const { post } = useRequest();

	const mutation = useMutation({
		mutationFn: async (request: Dtos.Decisioning.Decision.Create.Request) =>
			Dtos.Decisioning.Decision.Get.Response.schema.parse(
				await post<unknown>(`/v2/admin/surety/accounts/${accountId}/decisions`, request),
			),
		retry: false,
		onError: logError,
		onSuccess: (created) => {
			queryClient.setQueryData<Decision[]>(
				decisionsQueryKey(accountId),
				(items) => items && [created, ...items],
			);
		},
	});

	return {
		createDecision: mutation.mutate,
		createDecisionLoading: mutation.isPending,
	};
};
